import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Loader2, AlertCircle } from 'lucide-react';
import { ConversationProvider } from '@elevenlabs/react';
import { Button } from '@/components/ui/button';
import PageContainer from '@/components/layout/PageContainer';
import ConfirmDestructiveModal from '@/components/shared/ConfirmDestructiveModal.tsx';
import SessionBriefing from '@/components/simulation/SessionBriefing.tsx';
import MaestroAvatar from '@/components/simulation/MaestroAvatar.tsx';
import SessionFaceToFace from '@/components/simulation/SessionFaceToFace.tsx';
import SessionInterruptedModal from '@/components/simulation/SessionInterruptedModal.tsx';
import SessionDetail from '@/components/dashboard/SessionDetail.tsx';
import FeedbackSurveyBanner from '@/components/feedbackSurvey/FeedbackSurveyBanner.tsx';
import { useSimulationSession } from '@/hooks/useSimulationSession.ts';
import { maestros, getMaestroName, getMaestroTitle, getMaestroSubtitle, getMaestroDescription } from '@/types/maestro.ts';
import type { Maestro } from '@/types/maestro.ts';
import type { SimulationResponse, DocumentsReadiness } from '@/types/simulation.ts';
import simulationService from '@/services/simulationService.ts';
import { useAuth } from '@/context/AuthContext.tsx';
import { useAlert } from '@/context/AlertContext.tsx';

export default function SimulationPage() {
  return (
    <ConversationProvider>
      <SimulationPageInner />
    </ConversationProvider>
  );
}

function SimulationPageInner() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const { isSalesManager, user } = useAuth();
  const { showAlert } = useAlert();
  const [simulation, setSimulation] = useState<SimulationResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  // Pitch flow: chosen time target (F) + previous attempt's score for the retry delta (E).
  const [pitchTargetSeconds, setPitchTargetSeconds] = useState(60);
  const prevPitchScoreRef = useRef<number | null>(null);

  // If maestro was pre-selected (coming from sidebar), skip selection step
  const preselectedMaestroId = (location.state as { maestroId?: string } | null)?.maestroId;
  const preselectedMaestro = preselectedMaestroId
    ? maestros.find((m) => m.id === preselectedMaestroId) ?? null
    : null;

  const [selectedMaestro, setSelectedMaestro] = useState<Maestro>(preselectedMaestro ?? maestros[0]);
  const [maestroChosen, setMaestroChosen] = useState(!!preselectedMaestro);

  // Quick practice support — accept simulation data from location.state
  const quickPractice = (location.state as { quickPractice?: SimulationResponse } | null)?.quickPractice ?? null;

  const session = useSimulationSession();

  // Clear navigation state so refresh doesn't re-apply preselection
  useEffect(() => {
    if (preselectedMaestroId || quickPractice) {
      window.history.replaceState({}, '');
    }
  }, [preselectedMaestroId, quickPractice]);

  // Single source of truth for fetching the simulation — a manager fetches by
  // id; a salesperson may be the owner (by id) or only an assignee (fallback to
  // the assignments list). Both the initial load and the readiness poller use
  // this, so the poller never gets stuck on the path the initial load avoided.
  const fetchSimulation = useCallback(async (): Promise<SimulationResponse | undefined> => {
    if (!id) return undefined;
    if (isSalesManager) {
      return simulationService.getById(id);
    }
    try {
      return await simulationService.getById(id);
    } catch {
      const data = await simulationService.getMyAssignments();
      return data.find((s) => s.id === id);
    }
  }, [id, isSalesManager]);

  // Load simulation data (skip if quick practice)
  useEffect(() => {
    if (quickPractice) {
      setSimulation(quickPractice);
      setLoading(false);
      return;
    }

    if (!id) return;
    let cancelled = false;

    async function load() {
      try {
        setLoading(true);
        setError(null);
        const found = await fetchSimulation();
        if (!found) throw new Error(t('simulationPage.notFound'));
        if (!cancelled) setSimulation(found);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : t('simulationPage.loadError'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [id, quickPractice, fetchSimulation, t]);

  // While documents are still being processed, poll the simulation so the
  // readiness badge (and start gating) update live instead of being stuck on
  // "preparing" until a manual reload. Stops as soon as readiness is terminal.
  useEffect(() => {
    if (quickPractice || !id) return;
    if (simulation?.documentsReadiness !== 'Processing') return;

    let cancelled = false;
    const interval = setInterval(async () => {
      try {
        const fresh = await fetchSimulation();
        if (!cancelled && fresh) setSimulation(fresh);
      } catch {
        // Transient error — keep the last known state and retry next tick.
      }
    }, 4000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [id, quickPractice, fetchSimulation, simulation?.documentsReadiness]);

  // Notify the user the moment processing finishes (only on a real transition
  // out of "Processing", so an already-ready simulation doesn't pop a toast).
  const prevReadinessRef = useRef<DocumentsReadiness | undefined>(undefined);
  useEffect(() => {
    const readiness = simulation?.documentsReadiness;
    const prev = prevReadinessRef.current;
    prevReadinessRef.current = readiness;

    if (prev !== 'Processing' || !readiness || readiness === 'Processing') return;

    if (readiness === 'Ready') {
      showAlert(t('documents.toast.ready'), 'success');
    } else if (readiness === 'PartiallyFailed') {
      showAlert(t('documents.toast.partiallyFailed'), 'warning');
    } else if (readiness === 'Failed') {
      showAlert(t('documents.toast.failed'), 'error');
    }
  }, [simulation?.documentsReadiness, showAlert, t]);

  // Forced maestro — when simulation has assignedMaestroId, override selection
  useEffect(() => {
    if (!simulation?.assignedMaestroId) return;
    const forced = maestros.find((m) => m.id === simulation.assignedMaestroId);
    if (forced) {
      setSelectedMaestro(forced);
      setMaestroChosen(true);
    }
  }, [simulation]);

  // Navigation guard — warn before leaving during active session
  const isSessionActive = session.status === 'in_progress';

  useEffect(() => {
    if (!isSessionActive) return;

    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };

    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isSessionActive]);

  const handleBack = useCallback(() => {
    if (isSessionActive) {
      setShowLeaveConfirm(true);
      return;
    }
    navigate(isSalesManager ? '/admin/simulations' : '/');
  }, [isSessionActive, navigate, isSalesManager]);

  const handleConfirmLeave = useCallback(() => {
    setShowLeaveConfirm(false);
    navigate(isSalesManager ? '/admin/simulations' : '/');
  }, [navigate, isSalesManager]);

  // Interrupted-session modal — bloqueante, dos rutas seguras de salida.
  // Both paths complete the session in the backend so the report runs in background;
  // the routing afterwards is the only difference.
  const handleInterruptedViewReport = useCallback(async () => {
    await session.confirmFinish();
    // Status is now 'completed' — the page re-renders into SessionDetail automatically.
  }, [session]);

  const handleInterruptedBackToSimulations = useCallback(async () => {
    await session.confirmFinish();
    navigate(isSalesManager ? '/admin/simulations' : '/');
  }, [session, navigate, isSalesManager]);

  const handleStartSession = useCallback(async () => {
    if (!simulation) return;
    await session.startSession(simulation, selectedMaestro, !!quickPractice);
  }, [simulation, selectedMaestro, session, quickPractice]);

  const handleSelectMaestroAndContinue = useCallback((maestro: Maestro) => {
    setSelectedMaestro(maestro);
    setMaestroChosen(true);
  }, []);

  // Loading state
  if (loading) {
    return (
      <div>
        <div className="flex flex-col items-center justify-center py-32 text-muted-foreground">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="mt-3 text-sm">{t('simulationPage.loadingSimulation')}</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !simulation) {
    return (
      <div>
        <div className="flex flex-col items-center justify-center py-32 text-muted-foreground">
          <AlertCircle className="h-8 w-8 text-red-500" />
          <p className="mt-3 text-sm text-red-500">{error ?? t('simulationPage.notFound')}</p>
          <Button variant="link" onClick={() => navigate(isSalesManager ? '/admin/simulations' : '/')}>
            {t('simulationPage.backToSimulations')}
          </Button>
        </div>
      </div>
    );
  }

  // Completed — render the async report view (skeleton/progressive/failed via poller).
  if (session.status === 'completed') {
    if (session.sessionId) {
      return (
        <>
          <SessionDetail
            sessionId={session.sessionId}
            onBack={() => navigate(isSalesManager ? '/admin/simulations' : '/')}
            backLabel={isSalesManager ? t('simulationPage.backToSimulations') : t('mvpFlow.restart')}
            onNewAttempt={simulation.purpose === 'pitch' ? handleStartSession : undefined}
            previousScore={simulation.purpose === 'pitch' ? prevPitchScoreRef.current : null}
            onScoreLoaded={(score) => { prevPitchScoreRef.current = score; }}
          />
          {/* Dopo il risultato: chiediamo un feedback onesto (una volta per utente). */}
          <FeedbackSurveyBanner source={simulation.purpose === 'pitch' ? 'Pitch' : 'Simulation'} />
        </>
      );
    }

    return (
      <div>
        <div className="flex flex-col items-center justify-center py-32 text-muted-foreground">
          <AlertCircle className="h-8 w-8 text-yellow-500" />
          <h3 className="mt-4 text-lg font-semibold text-foreground">
            {t('simulationPage.sessionCompleted')}
          </h3>
          <p className="mt-2 max-w-md text-center text-sm">
            {t('simulationPage.reportUnavailable')}
          </p>
          <Button className="mt-6" onClick={() => navigate(isSalesManager ? '/admin/simulations' : '/')}>
            {t('simulationPage.backToSimulations')}
          </Button>
        </div>
      </div>
    );
  }

  // Maestro selection (before briefing)
  if (!maestroChosen) {
    return (
      <div>
        <main className="px-4 py-8 sm:px-6 lg:px-8">
          <PageContainer variant="focus" className="animate-fade-in">
            {/* Back */}
            <button
              type="button"
              onClick={handleBack}
              className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" />
              {t('simulationPage.backToSimulations')}
            </button>

            <div className="mb-8 text-center">
              <h1 className="font-display text-3xl text-foreground sm:text-4xl">
                {simulation.name}
              </h1>
              <p className="mt-2 text-muted-foreground">
                {t('simulationPage.chooseMaestro')}
              </p>
            </div>

            <div className="grid gap-4">
              {maestros.map((maestro) => (
                <button
                  key={maestro.id}
                  onClick={() => handleSelectMaestroAndContinue(maestro)}
                  className="group flex items-center gap-4 rounded-2xl border border-border bg-card p-5 text-left shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-lg"
                >
                  <MaestroAvatar maestro={maestro} size="lg" className="transition-transform duration-200 group-hover:scale-105" />
                  <div className="flex-1">
                    <p className="font-semibold" style={{ color: maestro.accentColor }}>
                      {getMaestroName(maestro)}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {getMaestroTitle(maestro)} — {getMaestroSubtitle(maestro)}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground/70">
                      {getMaestroDescription(maestro).split('. ')[0]}.
                    </p>
                  </div>
                  <div className="text-primary opacity-0 transition-opacity group-hover:opacity-100">
                    <ArrowLeft className="h-5 w-5 rotate-180" />
                  </div>
                </button>
              ))}
            </div>
          </PageContainer>
        </main>
      </div>
    );
  }

  // Briefing
  if (session.status === 'briefing') {
    return (
      <div>
        <main className="px-4 py-8 sm:px-6 lg:px-8">
          {/* Back */}
          <PageContainer variant="focus">
            <button
              type="button"
              onClick={handleBack}
              className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" />
              {t('simulationPage.backToSimulations')}
            </button>
          </PageContainer>
          <SessionBriefing
            simulation={simulation}
            maestro={selectedMaestro}
            onStart={handleStartSession}
            isQuickPractice={!!quickPractice}
            isStarting={session.isStarting}
            initError={session.initError}
            onDismissError={session.clearInitError}
            timeTarget={pitchTargetSeconds}
            onTimeTargetChange={setPitchTargetSeconds}
          />
        </main>
      </div>
    );
  }

  // Active session (in_progress, paused, finishing)
  return (
    <div className="flex h-[calc(100vh-7rem)] min-h-0 flex-col">
      <PageContainer variant="wide" className="flex w-full flex-1 min-h-0 flex-col animate-fade-in">
        {/* Back (with guard) */}
        <button
          type="button"
          onClick={handleBack}
          className="mb-4 inline-flex shrink-0 items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('simulationPage.exitSimulation')}
        </button>

        {/* Face-to-face conversation (two avatars, single live caption, no chat) */}
        <SessionFaceToFace
          maestro={selectedMaestro}
          personaName={session.personaName}
          userName={user ? `${user.firstName} ${user.lastName}`.trim() : t('report.vendorLabel', 'Venditore')}
          userAvatarUrl={user?.avatarUrl}
          messages={session.messages}
          isSpeaking={session.isSpeaking}
          isMuted={session.isMuted}
          isPaused={session.isPaused}
          elapsedSeconds={session.elapsedSeconds}
          targetSeconds={simulation.purpose === 'pitch' ? pitchTargetSeconds : null}
          voiceEnabled={session.voiceEnabled}
          disabled={session.status !== 'in_progress'}
          onToggleMute={session.toggleMute}
          onTogglePause={session.togglePause}
          onFinish={session.requestFinish}
          onSendTextMessage={session.sendTextMessage}
        />
      </PageContainer>

      {/* Finish confirmation modal — only for the manual user-driven path.
          The interrupted-by-disconnect path renders SessionInterruptedModal below. */}
      <ConfirmDestructiveModal
        isOpen={session.status === 'finishing' && !session.wasInterrupted}
        onClose={session.cancelFinish}
        onConfirm={session.confirmFinish}
        title={t('simulationPage.finishTitle')}
        description={t('simulationPage.finishMessage')}
        confirmLabel={t('simulationPage.finishConfirm')}
        cancelLabel={t('common.continue')}
      />

      {/* Interrupted-session modal — bloqueante, sin click-outside / Escape.
          Surfaces only on server-initiated disconnects (ElevenLabs drop, network error, etc.). */}
      <SessionInterruptedModal
        isOpen={session.status === 'finishing' && session.wasInterrupted}
        onViewReport={handleInterruptedViewReport}
        onBackToSimulations={handleInterruptedBackToSimulations}
      />

      {/* Leave confirmation modal */}
      <ConfirmDestructiveModal
        isOpen={showLeaveConfirm}
        onClose={() => setShowLeaveConfirm(false)}
        onConfirm={handleConfirmLeave}
        title={t('simulationPage.leaveTitle')}
        description={t('simulationPage.leaveConfirm')}
        confirmLabel={t('simulationPage.leaveConfirmCta')}
        cancelLabel={t('common.continue')}
      />

    </div>
  );
}
